/**
 * 13 Galactic Calendar System
 * 
 * A 13-month calendar with 28 days per month (364 days) plus "Day Out of Time"
 * Year can start on fixed date (July 26) or be lunar-conditioned
 */

import type { YearStartMode, Calendar13Config, Day13, Month13 } from './calendar13Types';
import { MONTH_NAMES, WEEKDAY_NAMES } from './calendar13Types';

import { DEFAULT_CONFIG } from './calendar13Types';

const DAY_MS = 1000 * 60 * 60 * 24;
const NEW_YEAR_ROLLOVER_MINUTE = 1;

const toLocalMidnight = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const isWithinNewYearRolloverBuffer = (date: Date, config: Calendar13Config) => {
  const fixedStart = config.fixedStartDate ?? DEFAULT_CONFIG.fixedStartDate!;
  const isStartDate =
    date.getMonth() === fixedStart.month - 1 &&
    date.getDate() === fixedStart.day;

  if (!isStartDate) return false;

  const totalMinutes = date.getHours() * 60 + date.getMinutes();
  return totalMinutes < NEW_YEAR_ROLLOVER_MINUTE;
};

// Convert a local Date (Y/M/D) to the UTC midnight milliseconds for that same
// local Y/M/D. Use the local year/month/date components so the UTC ms aligns
// with the intended local-day boundary.
const toUtcMidnightMs = (date: Date) =>
  Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());

const isLeapYear = (year: number) =>
  (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;

const countLeapDaysBetween = (start: Date, end: Date) => {
  const startTime = toUtcMidnightMs(start);
  const endTime = toUtcMidnightMs(end);
  if (endTime < startTime) return 0;

  let count = 0;
  for (let year = start.getUTCFullYear(); year <= end.getUTCFullYear(); year++) {
    if (!isLeapYear(year)) continue;
    const leapDay = Date.UTC(year, 1, 29);
    if (leapDay >= startTime && leapDay <= endTime) {
      count++;
    }
  }
  return count;
};

const advanceGalacticDays = (startUtcMs: number, galacticDays: number) => {
  let currentUtc = startUtcMs;

  for (let i = 0; i < galacticDays; i++) {
    const currentDate = new Date(currentUtc);
    const shouldSkipLeapDay =
      isLeapYear(currentDate.getUTCFullYear()) &&
      currentDate.getUTCMonth() === 1 &&
      currentDate.getUTCDate() === 28;

    currentUtc += shouldSkipLeapDay ? DAY_MS * 2 : DAY_MS;
  }

  return currentUtc;
};

const fromUtcMsToLocalMidnight = (utcMs: number) => {
  const date = new Date(utcMs);
  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
};

export { DEFAULT_CONFIG };

export type { Day13 } from './calendar13Types';

/**
 * Get the Gregorian date for the start of a Galactic year
 */
export function getYearStartDate(galacticYear: number, config: Calendar13Config = DEFAULT_CONFIG): Date {
  // Use the configured epoch year as the base
  const gregorianYear = config.epochYear + galacticYear - 1;

  return new Date(gregorianYear, config.fixedStartDate.month - 1, config.fixedStartDate.day);
}

/**
 * Convert Gregorian year to Galactic year
 */
export function gregorianToGalacticYear(gregorianYear: number, config: Calendar13Config = DEFAULT_CONFIG): number {
  return gregorianYear - config.epochYear + 1;
}

/**
 * Convert a Gregorian date to 13-calendar date
 */
export function gregorianToDay13(date: Date, config: Calendar13Config = DEFAULT_CONFIG): Day13 {
  // Keep a short rollover buffer at new year so Jul 26 00:00 still maps to
  // Day Out of Time, and Jul 26 00:01 starts the new galactic year.
  const boundaryAdjustedDate = isWithinNewYearRolloverBuffer(date, config)
    ? new Date(date.getTime() - DAY_MS)
    : date;

  // Normalize to UTC to avoid daylight saving gaps/overlaps shifting the day count
  // when comparing midnight timestamps across DST transitions. We still return the
  // original local date for display, but all calculations use UTC-midnight values.
  const inputDate = toLocalMidnight(boundaryAdjustedDate);
  const inputUtc = toUtcMidnightMs(inputDate);
  
  // Find which galactic year this falls in
  let galacticYear = gregorianToGalacticYear(inputDate.getFullYear(), config);
  let yearStart = getYearStartDate(galacticYear, config);
  let yearStartUtc = toUtcMidnightMs(yearStart);
  
  // Adjust if we're before the year start
  if (inputDate < yearStart) {
    galacticYear--;
    yearStart = getYearStartDate(galacticYear, config);
    yearStartUtc = toUtcMidnightMs(yearStart);
  }

  // Days since year start
  const diffTime = inputUtc - yearStartUtc;
  const rawDayOfYear = Math.floor(diffTime / DAY_MS);
  const leapAdjust = countLeapDaysBetween(yearStart, inputDate);
  const dayOfYear = rawDayOfYear - leapAdjust;
  
  // Day 364 (0-indexed: 364) is Day Out of Time
  if (dayOfYear === 364) {
    return {
      month: 0,
      day: 0,
      year: galacticYear,
      isOutOfTime: true,
      gregorianDate: inputDate,
      isLeapDay: false,
      weekday: -1,
    };
  }

  // Clamp to 364-day cycle by skipping leap days
  const clampedDayOfYear = Math.min(dayOfYear, 364);

  const month = Math.floor(clampedDayOfYear / 28) + 1;
  const day = (clampedDayOfYear % 28) + 1;
  const weekday = (day - 1) % 7;

  return {
    month,
    day,
    year: galacticYear,
    isOutOfTime: false,
    gregorianDate: inputDate,
    isLeapDay: inputDate.getMonth() === 1 && inputDate.getDate() === 29,
    weekday,
  };
}

/**
 * Convert a 13-calendar date to Gregorian
 */
export function day13ToGregorian(
  galacticYear: number,
  month: number,
  day: number,
  config: Calendar13Config = DEFAULT_CONFIG
): Date {
  const yearStart = getYearStartDate(galacticYear, config);
  const yearStartUtc = toUtcMidnightMs(yearStart);

  if (month === 0 && day === 0) {
    // Day Out of Time
    const utcDate = advanceGalacticDays(yearStartUtc, 364);
    return fromUtcMsToLocalMidnight(utcDate);
  }

  const daysFromStart = (month - 1) * 28 + (day - 1);
  const utcDate = advanceGalacticDays(yearStartUtc, daysFromStart);
  const localDate = fromUtcMsToLocalMidnight(utcDate);
  if (month === 1 && day === 1 && NEW_YEAR_ROLLOVER_MINUTE > 0) {
    localDate.setHours(0, NEW_YEAR_ROLLOVER_MINUTE, 0, 0);
  }
  return localDate;
}

/**
 * Get all days for a given galactic month
 */
export function getMonth13(
  galacticYear: number,
  month: number,
  config: Calendar13Config = DEFAULT_CONFIG
): Month13 {
  // Handle invalid month (e.g., 0 for Day Out of Time) by defaulting to month 1
  const safeMonth = month >= 1 && month <= 13 ? month : 1;
  const monthInfo = MONTH_NAMES[safeMonth - 1];
  const days: Day13[] = [];
  
  for (let day = 1; day <= 28; day++) {
    const gregorianDate = day13ToGregorian(galacticYear, safeMonth, day, config);
    days.push({
      month: safeMonth,
      day,
      year: galacticYear,
      isOutOfTime: false,
      gregorianDate,
      weekday: (day - 1) % 7,
    });
  }
  
  return {
    number: safeMonth,
    name: monthInfo.name,
    glyph: monthInfo.glyph,
    days,
  };
}

/**
 * Get current galactic year
 */
export function getCurrentGalacticYear(config: Calendar13Config = DEFAULT_CONFIG): number {
  const today = gregorianToDay13(new Date(), config);
  return today.year;
}

/**
 * Format a Day13 as a string
 */
export function formatDay13(day: Day13): string {
  if (day.isOutOfTime) {
    return "Day Out of Time";
  }
  const monthName = MONTH_NAMES[day.month - 1].name;
  return `${monthName} ${day.day}`;
}

/**
 * Get the current date in 13-calendar
 */
export function getToday13(config: Calendar13Config = DEFAULT_CONFIG): Day13 {
  return gregorianToDay13(new Date(), config);
}
